import { _isEmpty, _isEmptyString, _isNil, _isNotEmpty, _notNil, _toNumber } from '@/littledash';
import { NO_SPACES_REGEX, THREE_DECIMAL_PLACES_REGEX, VALID_CHARACTER_REGEX } from '@/validators.constants';
import type { MetadataFieldValueTypes } from '@/model/Metadata.model';

export const withinCharacterLimits = (value: string, min: number, max: number) => {
  return (max >= value.length && value.length >= min) || `Must be between ${min} and ${max} characters long.`;
};

export const containsValidCharacters = (value: string, message?: string) => {
  return (
    _isNotEmpty(value.match(VALID_CHARACTER_REGEX)) ||
    message ||
    "Consist of letters or numbers and contain only the following special characters:  _ - . ! * ' ( )"
  );
};

export const containsNoSpaces = (value: string) => {
  return _isNotEmpty(value.match(NO_SPACES_REGEX)) || 'Must not contain spaces.';
};

export const numericValidator = (value?: string | null, required: boolean = false) => {
  const val = value ?? '';
  if (_isNotEmpty(val)) {
    return !isNaN(val);
  }
  return !required;
};

export const decimalPlacesValidator = (name: string) => (value: number | null | undefined) => {
  if (_isNil(value) || _isEmpty(value) || THREE_DECIMAL_PLACES_REGEX.test(_toNumber(value, '').toString())) {
    return true;
  }
  return `${name} can't be more than three decimal places`;
};

export const valueRequired = (name: string, required: boolean) => (value: number | null | undefined) =>
  _notNil(value) && _isNotEmpty(value) ? true : required ? `${name} is required` : true;

export const isRequiredValidator = (fieldName: string, required: boolean) => (value: MetadataFieldValueTypes) => {
  if (!required) {
    return true;
  }

  if (_isNil(value) || _isEmpty(value)) {
    return `${fieldName} is required`;
  }

  if (typeof value === 'string') {
    return value.trim() !== '' ? true : `${fieldName} is required`;
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? true : `${fieldName} is required`;
  }

  return true;
};

export const nilOrGreaterThan =
  (name: string, greaterThan: number, required: boolean, units: string = '') =>
  (value: number | string | null | undefined) => {
    const valueAsNumber = Number(value);
    if (_isNil(value) || _isEmptyString(value) || isNaN(valueAsNumber)) {
      return required ? `${name} must be greater than ${greaterThan} ${units}` : true;
    }
    return valueAsNumber > greaterThan ? true : `${name} must be greater than ${greaterThan} ${units}`;
  };

// HandsOnTable validator functions
type HotValidatorCallback = (isValid: boolean) => void;
type HotValidator = (value: MetadataFieldValueTypes, callback: HotValidatorCallback) => void;

export const numericValidatorFactory = () => {
  return (value: MetadataFieldValueTypes, callback: HotValidatorCallback) => {
    const stringValue = typeof value === 'string' ? value : (value?.toString() ?? '');
    callback(numericValidator(stringValue));
  };
};

export const textValidatorFactory = (maxLength = 250) => {
  return (value: MetadataFieldValueTypes, callback: HotValidatorCallback) => {
    const stringValue = typeof value === 'string' ? value : (value?.toString() ?? '');

    // Simple text validation: check max length only (required handled by wrapper)
    let valid = true;
    if (_notNil(stringValue)) {
      valid = stringValue.length <= maxLength;
    }

    callback(valid);
  };
};

/**
 * Creates a composite HOT validator that combines required validation with field-specific validation
 * @param fieldName - Name of the field for error messages
 * @param isRequired - Whether the field is required
 * @param baseValidator - Optional base validator function for field-specific validation
 * @returns HOT validator function or undefined if no validation needed
 */
export const createHotRequiredValidator = (
  fieldName: string,
  isRequired: boolean,
  baseValidator?: HotValidator
): HotValidator => {
  return (value: MetadataFieldValueTypes, callback: HotValidatorCallback) => {
    // First check if value meets required constraint
    if (isRequired) {
      const requiredCheck = isRequiredValidator(fieldName, isRequired)(value);
      if (requiredCheck !== true) {
        callback(false);
        return;
      }
    }

    // If it passes the required check, use the base validator
    if (typeof baseValidator === 'function') {
      baseValidator(value, callback);
    } else {
      callback(true);
    }
  };
};
